import { useState } from 'react';

interface RevenueCalculatorProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function RevenueCalculator({ isOpen, onClose }: RevenueCalculatorProps) {
  const [formData, setFormData] = useState({
    bedrooms: '1',
    bathrooms: '1',
    propertyType: 'apartment',
    squareFeet: '800',
    areaType: 'suburban',
    amenities: [] as string[],
    seasonalPricing: 'yes'
  });
  const [result, setResult] = useState<{
    annual: number;
    monthly: number;
    daily: number;
    occupancyRate: number;
  } | null>(null);

  // Enhanced calculation with more factors
  const calculateRevenue = () => {
    // Base rates by property type (national average)
    const baseRates: Record<string, number> = {
      apartment: 95,
      house: 145,
      condo: 110,
      townhouse: 125,
      villa: 180
    };

    // Area type multipliers
    const areaMultipliers: Record<string, number> = {
      'urban': 1.35,
      'suburban': 1.10,
      'rural': 0.85,
      'beach': 1.50,
      'mountain': 1.40,
      'downtown': 1.45
    };

    // Bedroom multiplier
    const bedroomMultiplier: Record<string, number> = {
      '1': 1.0, '2': 1.4, '3': 1.8, '4': 2.2, '5': 2.6, '6+': 3.0
    };

    // Bathroom multiplier
    const bathroomMultiplier: Record<string, number> = {
      '1': 1.0, '1.5': 1.1, '2': 1.2, '2.5': 1.3, '3': 1.4, '3.5': 1.5, '4+': 1.6
    };

    // Square footage factor
    const sqft = parseInt(formData.squareFeet);
    const sqftFactor = sqft < 600 ? 0.85 : 
                       sqft < 1000 ? 1.0 : 
                       sqft < 1500 ? 1.15 : 
                       sqft < 2000 ? 1.30 : 
                       sqft < 3000 ? 1.50 : 1.75;

    // Amenities bonus (each amenity adds value)
    const amenitiesBonus = 1 + (formData.amenities.length * 0.05);

    // Seasonal pricing adjustment
    const seasonalFactor = formData.seasonalPricing === 'yes' ? 1.15 : 1.0;

    // Calculate base daily rate
    const propertyType = formData.propertyType as keyof typeof baseRates;
    const areaMultiplier = areaMultipliers[formData.areaType];
    const bedroomFactor = bedroomMultiplier[formData.bedrooms];
    const bathroomFactor = bathroomMultiplier[formData.bathrooms];

    const dailyRate = baseRates[propertyType] * 
                     areaMultiplier * 
                     bedroomFactor * 
                     bathroomFactor * 
                     sqftFactor * 
                     amenitiesBonus * 
                     seasonalFactor;

    // Occupancy rate based on area type and amenities
    const baseOccupancy = formData.areaType === 'urban' || formData.areaType === 'downtown' ? 0.72 :
                         formData.areaType === 'beach' || formData.areaType === 'mountain' ? 0.70 :
                         formData.areaType === 'suburban' ? 0.68 : 0.60;
    const amenitiesOccupancyBonus = formData.amenities.length * 0.02;
    const occupancyRate = Math.min(baseOccupancy + amenitiesOccupancyBonus, 0.85);

    // Calculate revenues
    const monthlyRevenue = dailyRate * 30 * occupancyRate;
    const annualRevenue = monthlyRevenue * 12;

    setResult({
      annual: annualRevenue,
      monthly: monthlyRevenue,
      daily: dailyRate,
      occupancyRate: occupancyRate * 100
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    calculateRevenue();
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setResult(null);
  };

  const handleAmenityToggle = (amenity: string) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
    setResult(null);
  };

  if (!isOpen) return null;

  const amenitiesList = [
    { id: 'pool', label: 'Pool', icon: 'ri-water-flash-line' },
    { id: 'hottub', label: 'Hot Tub', icon: 'ri-contrast-drop-line' },
    { id: 'gym', label: 'Gym', icon: 'ri-run-line' },
    { id: 'parking', label: 'Free Parking', icon: 'ri-parking-box-line' },
    { id: 'wifi', label: 'High-Speed WiFi', icon: 'ri-wifi-line' },
    { id: 'kitchen', label: 'Full Kitchen', icon: 'ri-restaurant-line' },
    { id: 'washer', label: 'Washer/Dryer', icon: 'ri-shirt-line' },
    { id: 'ac', label: 'Air Conditioning', icon: 'ri-temp-cold-line' },
    { id: 'workspace', label: 'Dedicated Workspace', icon: 'ri-computer-line' },
    { id: 'patio', label: 'Patio/Balcony', icon: 'ri-home-smile-line' }
  ];

  const areaTypeOptions = [
    { value: 'urban', label: 'Urban', icon: 'ri-building-line', description: 'City center, high foot traffic' },
    { value: 'suburban', label: 'Suburban', icon: 'ri-home-4-line', description: 'Residential neighborhoods' },
    { value: 'rural', label: 'Rural', icon: 'ri-plant-line', description: 'Countryside, quiet locations' },
    { value: 'beach', label: 'Beach', icon: 'ri-sun-line', description: 'Near ocean or waterfront' },
    { value: 'mountain', label: 'Mountain', icon: 'ri-mountain-line', description: 'Mountain towns, ski areas' },
    { value: 'downtown', label: 'Downtown', icon: 'ri-building-2-line', description: 'Central business district' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#FF6B6B] to-[#ff5252] p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h2 className="text-3xl font-bold text-white">Advanced Revenue Calculator</h2>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center text-white hover:bg-white/20 rounded-full transition-all cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
          <p className="text-white/90 mt-2">Get accurate revenue estimates based on your property's area type and features</p>
        </div>

        {/* Area Type Selection Bar */}
        <div className="bg-gray-50 border-b-2 border-gray-200 px-6 py-4">
          <p className="text-sm font-semibold text-gray-700 mb-3">Select Your Property Area Type:</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {areaTypeOptions.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setFormData({ ...formData, areaType: option.value });
                  setResult(null);
                }}
                className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all cursor-pointer ${
                  formData.areaType === option.value
                    ? 'border-[#FF6B6B] bg-[#FF6B6B]/10 shadow-md'
                    : 'border-gray-300 bg-white hover:border-[#FF6B6B]/50 hover:bg-gray-50'
                }`}
              >
                <i className={`${option.icon} text-2xl mb-1 ${
                  formData.areaType === option.value ? 'text-[#FF6B6B]' : 'text-gray-600'
                } w-6 h-6 flex items-center justify-center`}></i>
                <span className={`text-xs font-semibold ${
                  formData.areaType === option.value ? 'text-[#FF6B6B]' : 'text-gray-700'
                }`}>{option.label}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            <i className="ri-information-line w-4 h-4 flex items-center justify-center inline-block mr-1"></i>
            {areaTypeOptions.find(opt => opt.value === formData.areaType)?.description}
          </p>
        </div>

        {/* Content */}
        <div className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Number of Bedrooms */}
              <div>
                <label htmlFor="bedrooms" className="block text-sm font-semibold text-gray-900 mb-2">
                  Number of Bedrooms
                </label>
                <select
                  id="bedrooms"
                  name="bedrooms"
                  value={formData.bedrooms}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF6B6B] focus:border-transparent transition-all cursor-pointer"
                >
                  <option value="1">1 Bedroom</option>
                  <option value="2">2 Bedrooms</option>
                  <option value="3">3 Bedrooms</option>
                  <option value="4">4 Bedrooms</option>
                  <option value="5">5 Bedrooms</option>
                  <option value="6+">6+ Bedrooms</option>
                </select>
              </div>

              {/* Number of Bathrooms */}
              <div>
                <label htmlFor="bathrooms" className="block text-sm font-semibold text-gray-900 mb-2">
                  Number of Bathrooms
                </label>
                <select
                  id="bathrooms"
                  name="bathrooms"
                  value={formData.bathrooms}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF6B6B] focus:border-transparent transition-all cursor-pointer"
                >
                  <option value="1">1 Bathroom</option>
                  <option value="1.5">1.5 Bathrooms</option>
                  <option value="2">2 Bathrooms</option>
                  <option value="2.5">2.5 Bathrooms</option>
                  <option value="3">3 Bathrooms</option>
                  <option value="3.5">3.5 Bathrooms</option>
                  <option value="4+">4+ Bathrooms</option>
                </select>
              </div>

              {/* Property Type */}
              <div>
                <label htmlFor="propertyType" className="block text-sm font-semibold text-gray-900 mb-2">
                  Property Type
                </label>
                <select
                  id="propertyType"
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF6B6B] focus:border-transparent transition-all cursor-pointer"
                >
                  <option value="apartment">Apartment</option>
                  <option value="house">House</option>
                  <option value="condo">Condo</option>
                  <option value="townhouse">Townhouse</option>
                  <option value="villa">Villa</option>
                </select>
              </div>

              {/* Square Footage */}
              <div>
                <label htmlFor="squareFeet" className="block text-sm font-semibold text-gray-900 mb-2">
                  Square Footage
                </label>
                <input
                  type="number"
                  id="squareFeet"
                  name="squareFeet"
                  value={formData.squareFeet}
                  onChange={handleChange}
                  min="300"
                  max="10000"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF6B6B] focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Amenities Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                Property Amenities (Select all that apply)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {amenitiesList.map(amenity => (
                  <button
                    key={amenity.id}
                    type="button"
                    onClick={() => handleAmenityToggle(amenity.id)}
                    className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
                      formData.amenities.includes(amenity.id)
                        ? 'border-[#FF6B6B] bg-[#FF6B6B]/10'
                        : 'border-gray-300 bg-gray-50 hover:border-[#FF6B6B]/50'
                    }`}
                  >
                    <i className={`${amenity.icon} text-2xl ${
                      formData.amenities.includes(amenity.id) ? 'text-[#FF6B6B]' : 'text-gray-600'
                    } w-6 h-6 flex items-center justify-center mx-auto mb-1`}></i>
                    <span className={`text-xs font-medium ${
                      formData.amenities.includes(amenity.id) ? 'text-[#FF6B6B]' : 'text-gray-700'
                    }`}>{amenity.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Seasonal Pricing */}
            <div>
              <label htmlFor="seasonalPricing" className="block text-sm font-semibold text-gray-900 mb-2">
                Will you use seasonal pricing?
              </label>
              <select
                id="seasonalPricing"
                name="seasonalPricing"
                value={formData.seasonalPricing}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF6B6B] focus:border-transparent transition-all cursor-pointer"
              >
                <option value="yes">Yes - Adjust prices by season</option>
                <option value="no">No - Keep consistent pricing</option>
              </select>
            </div>

            {/* Calculate Button */}
            <button
              type="submit"
              className="w-full bg-[#FF6B6B] text-white font-semibold py-4 px-8 rounded-lg hover:bg-[#ff5252] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              Calculate Potential Revenue
            </button>
          </form>

          {/* Results */}
          {result !== null && (
            <div className="mt-8 p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-600 mb-2">Estimated Annual Revenue</p>
                <p className="text-5xl font-bold text-green-600 mb-4">
                  ${result.annual.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </p>
                
                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-white/60 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Monthly Average</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ${result.monthly.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </p>
                  </div>
                  <div className="bg-white/60 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Daily Rate</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ${result.daily.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </p>
                  </div>
                  <div className="bg-white/60 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Occupancy Rate</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {result.occupancyRate.toFixed(0)}%
                    </p>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mt-4 leading-relaxed">
                  * Based on current market rates for {areaTypeOptions.find(a => a.value === formData.areaType)?.label} properties and your property's specific features
                </p>
              </div>

              {/* How It's Calculated */}
              <div className="mt-6 pt-6 border-t border-green-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Calculation Factors:</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-0.5 w-5 h-5 flex items-center justify-center"></i>
                    <span><strong>Area Type:</strong> {areaTypeOptions.find(a => a.value === formData.areaType)?.label}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-0.5 w-5 h-5 flex items-center justify-center"></i>
                    <span><strong>Property Size:</strong> {formData.bedrooms} bed, {formData.bathrooms} bath, {formData.squareFeet} sq ft</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-0.5 w-5 h-5 flex items-center justify-center"></i>
                    <span><strong>Amenities:</strong> {formData.amenities.length} premium features selected</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-0.5 w-5 h-5 flex items-center justify-center"></i>
                    <span><strong>Pricing Strategy:</strong> {formData.seasonalPricing === 'yes' ? 'Seasonal optimization' : 'Consistent pricing'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-0.5 w-5 h-5 flex items-center justify-center"></i>
                    <span><strong>Market Data:</strong> Based on 12-month average from comparable Airbnb listings</span>
                  </li>
                </ul>
              </div>

              {/* CTA */}
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600 mb-4">Ready to turn this potential into reality?</p>
                <a
                  href="/contact"
                  className="inline-block bg-[#FF6B6B] text-white font-semibold py-3 px-8 rounded-lg hover:bg-[#ff5252] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
                >
                  Get Your Free Consultation
                </a>
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 leading-relaxed">
              <strong>Disclaimer:</strong> This calculator provides estimates based on current market data, property characteristics, and area type factors. Actual revenue may vary based on property condition, photos quality, listing optimization, management quality, seasonality, local events, and market conditions. For a detailed revenue analysis specific to your property, schedule a free consultation with our team.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
